
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const useImageUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const checkBucketAccess = async (): Promise<boolean> => {
    try {
      // Simple bucket access check
      const { error } = await supabase.storage
        .from('blog-images')
        .list('', { limit: 1 });

      if (error) {
        console.error('Bucket access error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error checking bucket access:', error);
      return false;
    }
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    if (!file) return null;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Check authentication
      const { data: { user } } = await supabase.auth.getUser();
      console.log('Current user:', user?.id, user?.email);

      if (!user) {
        toast.error('You must be logged in to upload images');
        return null;
      }

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `blog-${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

      console.log('Uploading file:', fileName, 'Size:', file.size, 'Type:', file.type);

      // Upload file to Supabase storage
      const { data, error } = await supabase.storage
        .from('blog-images')
        .upload(fileName, file);

      if (error) {
        console.error('Upload error details:', error);

        // Provide more specific error messages
        if (error.message.includes('Bucket not found')) {
          toast.error('Blog images storage bucket not found. Please contact an administrator.');
        } else if (error.message.includes('not allowed') || error.message.includes('policy')) {
          toast.error('You do not have permission to upload images. Please check storage policies.');
        } else if (error.message.includes('size')) {
          toast.error('File size too large. Maximum size is 5MB.');
        } else if (error.message.includes('duplicate') || error.message.includes('already exists')) {
          toast.error('File already exists. Please try again.');
        } else {
          toast.error(`Upload failed: ${error.message}`);
        }
        return null;
      }

      console.log('Upload successful:', data);

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('blog-images')
        .getPublicUrl(fileName);

      console.log('Public URL:', urlData);

      if (urlData?.publicUrl) {
        toast.success('Image uploaded successfully');
        return urlData.publicUrl;
      }

      toast.error('Failed to get public URL for uploaded image');
      return null;
    } catch (error) {
      console.error('Unexpected upload error:', error);
      toast.error(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteImage = async (imageUrl: string): Promise<boolean> => {
    if (!imageUrl) return false;

    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = fileName; // Files are stored in bucket root

      const { error } = await supabase.storage
        .from('blog-images')
        .remove([filePath]);

      if (error) {
        console.error('Delete error:', error);
        toast.error('Failed to delete image');
        return false;
      }

      toast.success('Image deleted successfully');
      return true;
    } catch (error) {
      console.error('Unexpected delete error:', error);
      toast.error('Failed to delete image');
      return false;
    }
  };

  return {
    uploadImage,
    deleteImage,
    checkBucketAccess,
    isUploading,
    uploadProgress,
  };
};
